import { useCallback } from 'react';
import {
    INITIAL_FLOW_STEP_CONFIGS,
    HOMEOWNER_INDICATOR_STEP_COUNT,
} from './steps/initial-flow-steps';
import {
    useHDERegistrationForm,
    type FilterVisibleStepsContext,
    type GetInitialDataContext,
} from '../../hooks/useHDERegistrationForm';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';

type ApplicationType = 'Homeowner' | 'Professional' | 'Exhibitor';

interface HDEInitialFlowProps {
    stageConfig: StageConfiguration | null;
    locationOptions: unknown[];
    layout?: 'single' | 'split';
    onClose?: () => void;
    onStepChange?: (stepId: string) => void;
    onApplicationTypeSelected: (type: ApplicationType) => void;
    /** When true, skip the intro and start at the application-type step */
    startAtApplicationType?: boolean;
}

interface FormData {
    applicationType: ApplicationType | null;
}

const INITIAL_FORM_DATA: FormData = {
    applicationType: null,
};

/**
 * Initial HDE flow: intro + application type selection.
 * When user selects Homeowner, Professional, or Exhibitor, calls onApplicationTypeSelected.
 */
export function HDEInitialFlow({
    stageConfig,
    locationOptions,
    layout = 'split',
    onClose,
    onStepChange,
    onApplicationTypeSelected,
    startAtApplicationType = false,
}: HDEInitialFlowProps) {
    const filterVisibleSteps = useCallback(
        (
            configs: typeof INITIAL_FLOW_STEP_CONFIGS,
            _context: FilterVisibleStepsContext,
        ) => configs,
        [],
    );

    const getInitialDataForStep = useCallback(
        (stepId: string, formData: FormData, _ctx: GetInitialDataContext) => {
            if (stepId === 'application-type') {
                return {
                    applicationType: formData.applicationType,
                    onApplicationTypeSelected,
                };
            }
            return undefined;
        },
        [onApplicationTypeSelected],
    );

    const buildSubmitPayload = useCallback(() => new FormData(), []);

    const applicationTypeIndex = INITIAL_FLOW_STEP_CONFIGS.findIndex(
        (s) => s.id === 'application-type',
    );

    const form = useHDERegistrationForm<FormData>({
        initialFormData: INITIAL_FORM_DATA,
        stepConfigs: INITIAL_FLOW_STEP_CONFIGS,
        layout,
        filterVisibleSteps,
        getInitialDataForStep,
        buildSubmitPayload,
        locationOptions,
        stageConfig: stageConfig ?? undefined,
        onClose,
        onStepChange,
        defaultTotalStepsForIndicator: HOMEOWNER_INDICATOR_STEP_COUNT,
        initialStepIndex:
            startAtApplicationType && applicationTypeIndex >= 0
                ? applicationTypeIndex
                : 0,
    });

    return form.renderForm();
}
